import componentService, { componentID, ComponentKeys } from "./component-service";
import rawData from "../../../resources/generated/schema-supported-topologies.json";
import unSupportedTopologiesRawData from "../../../resources/generated/unsupported-topologies.json";
import { createAppUtils } from "../../../src/modules/apps/pages/create-app-page-templates/create-app-page-utils";
import { TopologyInfoInput, TopologyInfo } from "./Types/topology-info-service-types";
import adapterSecurityCheck from "../securityService/adapter-security-check";
import ComponentNames from "../../../resources/generated/components.json";

/**
 *  Components mapped as a typed record for safer access.
 */
const schemaSupportedToplogies = (rawData as unknown) as string[];
const unSupportedTopologies = (unSupportedTopologiesRawData as unknown) as string[];

/**
 * Type representing Template Info service.
 */
interface TemplateInfoServiceType {
    isSchemaSupportedTopology: (source: componentID, target: componentID) => boolean;
    isTopologyUnSupported: (source: componentID, target: componentID) => boolean;
    generateTopologyInfo: ({ source, target, appType }: TopologyInfoInput) => TopologyInfo;
    _checkIsAdapterValid: (source: string, target: string) => boolean;
    getTopologyInfoFromURL: (url: string) => { source: string; target: string };
}
/**
 * SERVICE
 *
 * Service for accessing unsupported and schema conversion supported topologies.
 */
class TemplateInfoService implements TemplateInfoServiceType {
    /**
     * This function generates the search tag.
     * The search tag should match the format present in the template-details-info.json .
     *
     */
    private getSearchTag(source: componentID, target: componentID) {
        const searchTag = source + "-" + target;
        return searchTag;
    }
    /**
     * Checks if the given source and target has schema conversion supported.
     *
     * @param source
     * Need to pass the source (that is the component Name)
     * @param target
     * Need to pass the target (that is the component Name)
     *
     * @returns `boolean`
     * If Schema conversion supported returns true this returns false
     */
    isSchemaSupportedTopology(source: componentID, target: componentID): boolean {
        const searchTag = this.getSearchTag(source, target);
        return schemaSupportedToplogies.some(topology => topology.toUpperCase() === searchTag.toUpperCase());
    }
    /**
     * Checks if the given source and target combination is supported.
     *
     * @param source
     * Need to pass the source (that is the component Name)
     * @param target
     * Need to pass the target (that is the component Name)
     *
     * @returns `boolean`
     * If Topology is not supported returns true else returns false
     */
    isTopologyUnSupported(source: componentID, target: componentID): boolean {
        if (!target) return false;

        const searchTag = this.getSearchTag(source, target);

        /**
         * If these targets are selected, the topology is not supported.
         */
        const excludedTargets = [
            ComponentNames.TeamsAlertAdapter[ComponentKeys.ComponentName],
            ComponentNames.Slack[ComponentKeys.ComponentName]
        ] as Readonly<componentID[]>;

        if (excludedTargets.includes(target)) {
            return true;
        }

        return unSupportedTopologies.includes(searchTag);
    }

    /**
     * This function replicates generateTemplate.js
     *
     * @param source
     * Need to pass the source (that is the component Name)
     * @param target
     * Need to pass the target (that is the component Name)
     * @param appType
     * Need to pass the appType. Either of IL, CDC, AUTOMATED PIPELINE, AUTOMATED MODE
     * @returns
     * Returns an object containing topology information.
     * - `url`: string
     * - `title`: string
     * - `fromIcon`: string
     * - `toIcon`: string
     * - `sourceAdapter`: string
     * - `targetAdapter`: string
     * - `steps`: array
     * - `uses`: array
     * - `enabledByLicense`: boolean
     */
    generateTopologyInfo({ source, target, appType = null }) {
        if (!(this._checkIsAdapterValid(source) && this._checkIsAdapterValid(target))) {
            return;
        }
        const url = createAppUtils.generateTemplateURL(source, target, appType);
        const title = `${componentService.getHumanReadableName(source)} to ${componentService.getHumanReadableName(
            target
        )}`;
        const fromIcon = componentService.getIconClassName(source);
        const toIcon = componentService.getIconClassName(target);
        const sourceAdapter = componentService.getAdapter(source);
        const targetAdapter = componentService.getAdapter(target) || null;
        const steps = target === "Striim" ? [source] : [source, target];
        const uses = [sourceAdapter, targetAdapter].filter(Boolean);
        const enabledByLicense = adapterSecurityCheck.checkIfEnabledSync([sourceAdapter, targetAdapter]);

        return {
            url,
            title,
            fromIcon,
            toIcon,
            sourceAdapter,
            targetAdapter,
            steps,
            uses,
            enabledByLicense
        };
    }
    /**
     * This function checks if the adapter is valid
     *
     * @param source
     * Need to pass the source (that is the component Name)
     * @param target
     * Need to pass the target (that is the component Name)
     * @returns `boolean`
     */
    _checkIsAdapterValid(adapter) {
        const components = componentService.getIDs();
        return components.includes(adapter);
    }

    /**
     * This function returns the source and target from the url.
     * @param url
     * Need to pass the url
     * @returns `Object`
     * Returns an object containing source and target.
     */
    getTopologyInfoFromURL(url) {
        let [source, target] = url.split("-to-");
        target = target.replace("-automated-ILCDC", "");
        return { source, target };
    }

    /**
     * This function returns the templates for the app adapter home page.
     * @returns `Array`
     * Returns an array of templates.
     */
    getAppAdapterHomePageTemplates() {
        const sourceTargetList = [
            { source: "StripeReader", target: "BigQuery" },
            { source: "ZendeskReader", target: "BigQuery" },
            { source: "HubSpotReader", target: "BigQuery" }
        ];
        let homePageTemplates = sourceTargetList
            .map(item => this.generateTopologyInfo(item))
            .sort((first, second) => {
                return first.enabledByLicense ? -1 : 0;
            });
        return homePageTemplates;
    }
}

/**
 * Singleton instance of the ComponentService.
 */
const templateInfoService = new TemplateInfoService();
export default templateInfoService;
